import {
  BadRequestError,
  ForbiddenError,
  HttpErrors,
  UnauthorizedError,
  UnExpectedError,
} from '@omecare/utils';
import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ZodError } from 'zod';
import { I18nTranslate } from './i18n';
import { AppRouter } from './router';
import { getErrorToastParams } from './utils/errors/get-error-toast-params';
import { getErrorType } from './utils/errors/get-error-type';

export const errorTranslationKeyOf = (key: string) => {
  return `http.errors.${key}`;
};

const toastrHttpError = ({
  error,
  t,
}: {
  error: HttpErrors;
  t: I18nTranslate;
}) => {
  const errorType = getErrorType(error);

  if (!errorType) {
    // No business-error type on the payload (generic/framework error). The
    // caller falls back to the generic `unexpected` toast — nothing to log.
    return false;
  }

  if (errorType === 'agenda_slot_booking_conflict') {
    return false;
  }

  // Retrieve error type from api
  // Display translation if exists, enriched with any type-specific params (e.g.
  // the accounting validate-report error lists the offending slots).
  const errorTradKey = errorTranslationKeyOf(errorType);
  const errorTradParams = getErrorToastParams({ error, errorType, t });
  const errorTrad = t(errorTradKey, errorTradParams);
  const isTradExists = errorTrad !== errorTradKey;

  if (isTradExists) {
    toast.error(errorTrad);
    return true;
  } else {
    // A recognizable business type with no translation — a genuine i18n gap.
    console.error('Api error type trad not found', {
      key: errorTradKey,
      data: error.data,
    });
    return false;
  }

  // @end
};

const handleGlobalErrors =
  ({ router, t }: { router: AppRouter; t: I18nTranslate }) =>
  (
    error: (HttpErrors | ZodError | Error) & { skipGlobalHandling?: boolean },
  ) => {
    if (error instanceof ZodError) {
      console.error('Zod::', error);
      return;
    }

    if (error instanceof UnauthorizedError) {
      toastrHttpError({ error, t });
      return router.navigate('/login');
    }

    if (error instanceof ForbiddenError || error instanceof BadRequestError) {
      toastrHttpError({ error, t });
      return;
    }

    if (error instanceof UnExpectedError) {
      // Server errors may still carry a translatable business type (e.g.
      // dazz_unreachable) — fall back to the generic message otherwise
      const displayed = toastrHttpError({ error, t });
      if (!displayed) {
        toast.error(t(errorTranslationKeyOf('unexpected')));
      }
      return;
    }

    console.error('UnExpectedError or not handle error', error);
    return;
  };

export const createQueryClient = ({
  router,
  t,
}: {
  router: AppRouter;
  t: I18nTranslate;
}) => {
  const queryClient = new QueryClient({
    queryCache: new QueryCache({
      onError: handleGlobalErrors({ router, t }),
    }),
    mutationCache: new MutationCache({
      onError: handleGlobalErrors({ router, t }),
    }),
  });

  return queryClient;
};
